<template>
  <div class="playground-chat-wrapper">
    <!-- Chat Window -->
    <transition name="slide-up">
      <div v-if="isOpen" class="chat-window">
        <!-- Header -->
        <div class="chat-header">
          <div class="header-info">
            <div class="bot-avatar">
              <img :src="botAvatar" alt="Bot" />
            </div>
            <div class="header-text">
              <h3>{{ botName }}</h3>
              <span class="status-indicator">
                <span class="dot"></span> En ligne
              </span>
            </div>
          </div>
          <div class="header-actions">
            <button
              @click="showLogs = !showLogs"
              class="action-btn logs-btn"
              :class="{ 'has-error': hasGriotError, 'active': showLogs }"
              title="Logs Griot AI (diagnostic)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
              <span v-if="griotErrorCount > 0" class="logs-badge">{{ griotErrorCount }}</span>
            </button>
            <button @click="resetChat" class="action-btn" title="Redémarrer la conversation">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
            <button @click="toggleChat" class="action-btn close-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Messages Area -->
        <div class="messages-area" ref="messagesContainer">
          <div v-if="messages.length === 0" class="empty-state">
            <p>Démarrer un conversation...</p>
          </div>
          
          <div 
            v-for="(msg, index) in messages" 
            :key="index" 
            class="message-row"
            :class="msg.sender"
          >
            <div class="message-bubble" :class="msg.sender">
              <!-- Text Content -->
              <div v-if="msg.type === 'text'" class="message-text">
                {{ msg.content }}
              </div>

              <!-- Image Content -->
              <div v-if="msg.type === 'image'" class="message-image">
                <img :src="msg.content" alt="Image envoyée" />
              </div>

              <!-- Buttons (Interactive) -->
              <div v-if="msg.type === 'buttons'" class="message-buttons">
                <p v-if="msg.content" class="message-text">{{ msg.content }}</p>
                <div class="buttons-grid">
                  <button 
                    v-for="btn in msg.options" 
                    :key="btn.id"
                    class="chat-option-btn"
                    @click="handleOptionClick(btn, msg)"
                    :disabled="btn.clicked"
                  >
                    {{ btn.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isTyping" class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>



        <!-- Quick Actions -->
        <div class="quick-actions" v-if="startBlockId || true" style="padding: 0 16px 8px; display: flex; gap: 8px; flex-wrap: wrap;">
          <button 
            @click="sendQuickAction('<restart>')" 
            class="quick-action-btn"
            title="Redémarrer la conversation"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            Restart
          </button>
          
          <button 
            v-if="startBlockId" 
            @click="sendQuickAction(`<goto:${startBlockId}>`)" 
            class="quick-action-btn"
            :title="'Aller au bloc: ' + (startBlockName || startBlockId)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            Go to: {{ startBlockName || startBlockId }}
          </button>
        </div>

        <!-- Griot AI Logs Panel (diagnostic) -->
        <transition name="slide-up">
          <div v-if="showLogs" class="griot-logs-panel">
            <div class="griot-logs-header">
              <span class="griot-logs-title">
                Logs Griot AI
                <span class="griot-logs-count">{{ filteredGriotLogs.length }}</span>
              </span>
              <div class="griot-logs-controls">
                <select v-model="logLevelFilter" class="griot-logs-filter">
                  <option value="ALL">Tous</option>
                  <option value="DEBUG">Debug+</option>
                  <option value="INFO">Info+</option>
                  <option value="WARN">Warn+</option>
                  <option value="ERROR">Erreurs</option>
                </select>
                <button class="griot-logs-clear" @click="clearGriotLogs" title="Effacer les logs">Vider</button>
              </div>
            </div>
            <div class="griot-logs-body">
              <div v-if="filteredGriotLogs.length === 0" class="griot-logs-empty">
                Aucun log pour ce filtre. Envoyez un message au bot pour générer des logs Griot.
              </div>
              <div
                v-for="(log, i) in filteredGriotLogs"
                :key="i"
                class="griot-log-line"
                :class="'level-' + log.level.toLowerCase()"
              >
                <span class="griot-log-time">{{ formatLogTime(log.ts) }}</span>
                <span class="griot-log-level" :class="'level-' + log.level.toLowerCase()">{{ log.level }}</span>
                <span class="griot-log-step" v-if="log.step">{{ log.step }}</span>
                <span class="griot-log-msg">{{ log.message }}</span>
                <span class="griot-log-data" v-if="log.data">{{ formatLogData(log.data) }}</span>
              </div>
            </div>
          </div>
        </transition>

        <!-- Input Area -->
        <div class="input-area">
          <input
            v-model="userInput"
            @keyup.enter="sendMessage"
            type="text"
            placeholder="Tapez un message..."

          />
          <button 
            @click="sendMessage" 
            class="send-btn"
            :disabled="!userInput.trim()"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    </transition>

    <!-- Floating Bubble Button -->
    <button class="chat-bubble-btn" @click="toggleChat" :class="{ 'active': isOpen }">
      <span v-if="!isOpen" class="icon-wrapper">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </span>
      <span v-else class="icon-wrapper">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </span>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';

const props = defineProps({
  chatbot: {
    type: Object,
    default: () => ({ name: 'Chatbot' })
  },
  botAvatarUrl: {
    type: String,
    default: null
  },
  blocks: {
    type: Array,
    default: () => []
  },
  startBlockId: {
    type: String,
    default: null
  },
  startBlockName: {
    type: String,
    default: null
  }
});

const isOpen = ref(false);
const messages = ref([]);
const userInput = ref('');
const isTyping = ref(false);
const isWaitingForInput = ref(false);

// --- Griot AI Logs (panneau de diagnostic du Playground) ---
// Liste plate d'entrées de log { ts, level, step, message, data, requestId, route }
// collectées par session de test, pour comprendre pourquoi le bot répond ou non.
const griotLogEntries = ref([]);
const showLogs = ref(false);
const logLevelFilter = ref('ALL'); // ALL | DEBUG | INFO | WARN | ERROR

// Aplatit les blocs { route, requestId, logs: [...] } renvoyés par le backend
// en entrées individuelles, en conservant la corrélation requestId/route.
const ingestGriotLogs = (blocks) => {
  for (const block of blocks) {
    const baseRoute = block.source || block.route || 'griot';
    const reqId = block.requestId || null;
    const logs = Array.isArray(block.logs) ? block.logs : [];
    for (const e of logs) {
      griotLogEntries.value.push({
        ts: e.ts || new Date().toISOString(),
        level: (e.level || 'INFO').toUpperCase(),
        step: e.step || '',
        message: e.message || '',
        data: e.data,
        route: baseRoute,
        requestId: reqId,
        elapsed_ms: e.elapsed_ms
      });
    }
  }
  // Garder un volume raisonnable (sessions longues)
  if (griotLogEntries.value.length > 500) {
    griotLogEntries.value = griotLogEntries.value.slice(-500);
  }
};

const LEVEL_ORDER = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };

const filteredGriotLogs = computed(() => {
  if (logLevelFilter.value === 'ALL') return griotLogEntries.value;
  const min = LEVEL_ORDER[logLevelFilter.value] ?? 0;
  return griotLogEntries.value.filter(e => (LEVEL_ORDER[e.level] ?? 1) >= min);
});

const hasGriotError = computed(() => griotLogEntries.value.some(e => e.level === 'ERROR'));
const griotErrorCount = computed(() => griotLogEntries.value.filter(e => e.level === 'ERROR').length);

const clearGriotLogs = () => { griotLogEntries.value = []; };

const formatLogTime = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour12: false });
  } catch (e) { return ''; }
};

const formatLogData = (data) => {
  if (data === undefined || data === null) return '';
  try { return JSON.stringify(data); } catch (e) { return String(data); }
};

// Engine State
const currentBlock = ref(null);
const currentFlowIndex = ref(0);
const executionQueue = ref([]); // For branching logic if needed

const botName = computed(() => props.chatbot?.bot_name || 'Assistant');
const botAvatar = computed(() => {
  if (props.botAvatarUrl) return props.botAvatarUrl;
  return props.chatbot?.avatar || 'https://ui-avatars.com/api/?name=Bot&background=0E8A3E&color=fff';
});

// --- Engine Logic ---

const findBlockById = (id) => {
  if (!id) return null;
  // Handle both _id and idblock
  return props.blocks.find(b => b.idblock === id || b._id === id);
};

const resetChat = () => {
  messages.value = [];
  isWaitingForInput.value = false;
  userInput.value = '';
  clearGriotLogs();
  
  // Determine start block: provided prop, or 'welcome', or first block
  let start = null;
  // Use startBlockId logic only if we actually want to restart from THAT block, 
  // currently resetChat implies restarting the whole conversation or the current flow?
  // User asked to restart with <restart> which implies welcome flow usually.
  
  // Try to find 'welcome' block first for a true reset
  start = props.blocks.find(b => b.category === 'welcome' || b.idblock === 'welcome');
  
  if (!start && props.startBlockId) {
    start = findBlockById(props.startBlockId);
  }
  
  if (!start) {
    start = props.blocks.find(b => b.type === 'default_block') || props.blocks[0];
  }

  if (start) {
    sessionStorage.removeItem(`sim_chat_${props.chatbot._id || props.chatbot.id}`); // Clear storage on reset
    runBlock(start);
  } else {
    addSystemMessage("Aucun bloc de démarrage trouvé.");
  }
};

const runBlock = async (block) => {
  console.log("Running block:", block.blockname);
  currentBlock.value = block;
  currentFlowIndex.value = 0;
  await processNextElement();
};

const processNextElement = async () => {
  if (!currentBlock.value || !currentBlock.value.flow) return;
  
  if (currentFlowIndex.value >= currentBlock.value.flow.length) {
    console.log("End of block flow");
    return;
  }

  const element = currentBlock.value.flow[currentFlowIndex.value];
  currentFlowIndex.value++;

  await executeElement(element);
  
  // Automatically continue if not waiting for input
  if (!isWaitingForInput.value && currentBlock.value) {
    await processNextElement();
  }
};

const executeElement = async (element) => {
  isTyping.value = true;
  await new Promise(r => setTimeout(r, 600)); // Simulate thinking
  isTyping.value = false;

  console.log("Executing element:", element.type);

  switch (element.type) {
    case 'text':
      addBotMessage(element.content, 'text');
      break;

    case 'image':
      addBotMessage(element.mediaUrl || element.content, 'image');
      break;

    case 'text_button':
      // Check if buttons exist
      const buttons = element.buttons || [];
      addBotMessage(element.content, 'buttons', buttons.map(b => ({
        id: b.id,
        action: b,
        clicked: false,
        replyType: 'button_reply'
      })));
      isWaitingForInput.value = true; // Buttons require input/selection
      break;
      
    case 'list':
       // Simplified list Representation
      const rows = element.rows || element.sections?.[0]?.rows || []; 
       addBotMessage(element.content || element.title || "Veuillez choisir une option :", 'buttons', rows.map(r => ({
          id: r.id,
          label: r.title,
          action: r,
          replyType: 'list_reply'
       })));
       isWaitingForInput.value = true;
       break;

    case 'answer':
      // Stop and wait for user text input
      isWaitingForInput.value = true;
      break;

    case 'redirect':
      // Redirect to another block
      const targetId = element.redirect_to || element.targetId;
      if (targetId) {
        const nextBlock = findBlockById(targetId);
        if (nextBlock) {
          await runBlock(nextBlock);
        } else {
           addSystemMessage(`Erreur: Bloc de redirection introuvable (${targetId})`);
        }
      }
      break;
      
    case 'delay':
       // Wait for specified time (capped at 2s for simulation to be fast)
       const delay = Math.min((element.duration || 1) * 1000, 3000); 
       isTyping.value = true;
       await new Promise(r => setTimeout(r, delay));
       isTyping.value = false;
       break;

    default:
      console.warn("Unsupported element type in simulator:", element.type);
      // addSystemMessage(`[Element non supporté: ${element.type}]`);
  }
};

// --- Messaging Logic ---

const addBotMessage = (content, type = 'text', options = null) => {
  // Generate a simulated ID for the bot message to reference in replies
  const messageId = "wamid.HBgMM" + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 7).toUpperCase();
  
  messages.value.push({
    sender: 'bot',
    id: messageId,
    type,
    content,
    options,
    timestamp: new Date()
  });
  scrollToBottom();
};

const addUserMessage = (content) => {
  messages.value.push({
    sender: 'user',
    type: 'text',
    content,
    timestamp: new Date()
  });
  scrollToBottom();
};

const addSystemMessage = (content) => {
  messages.value.push({
    sender: 'system',
    type: 'text',
    content,
    timestamp: new Date()
  });
  scrollToBottom();
};


import axios from 'axios';

const sendQuickAction = async (action) => {
  userInput.value = action;
  await sendMessage();
};

const sendMessage = async () => {
  if (!userInput.value.trim()) return;
  
  const text = userInput.value;
  userInput.value = '';
  isWaitingForInput.value = false;
  
  addUserMessage(text);

  // Command handling (Client-side overrides) - DISABLED to let Backend handle it
  /*
  if (text.startsWith('<goto:')) {
    const blockId = text.substring(6, text.length - 1);
    const block = findBlockById(blockId);
    if (block) {
       addSystemMessage(`Navigation vers: ${block.displayName || block.blockname || blockId}`);
       runBlock(block); // Keep client-side for navigation shortcuts
    } else {
       addSystemMessage(`Bloc introuvable: ${blockId}`);
    }
    return;
  }
  
  if (text === '<restart>') {
    resetChat();
    return;
  }
  */

  // Backend Simulation Call
  await sendToBackendSimulation({
      type: 'text',
      text: { body: text }
  });
};

const sendToBackendSimulation = async (messagePayload) => {
    isTyping.value = true;
    try {
        const ownerPhone = getOwnerPhone();
        const chatbotId = props.chatbot._id || props.chatbot.id;
        
        // Construct WhatsApp Webhook Payload
        const payload = {
            object: "whatsapp_business_account",
            entry: [{
                id: props.chatbot.phone_number_id || "106273332517016", // Specific ID from user request example
                changes: [{
                    value: {
                        messaging_product: "whatsapp",
                        metadata: {
                            display_phone_number: props.chatbot.display_phone_number || "221761758092",
                            phone_number_id: props.chatbot.phone_number_id || "106273332517016"
                        },
                        contacts: [{
                            profile: { name: "Sim User" },
                            wa_id: ownerPhone
                        }],
                        messages: [{
                            from: ownerPhone,
                            id: "wamid.sim." + Date.now(),
                            timestamp: Math.floor(Date.now() / 1000).toString(),
                            ...messagePayload
                        }]
                    },
                    field: "messages"
                }]
            }]
        };

      /*
      // Production URL
      const response = await axios.post(
        `https://webhook.peelo.io/pipeline/simulator/message/${chatbotId}`, 
        payload
      );
      */
      
      // Local Test URL
      const response = await axios.post(
        `http://localhost:1436/pipeline/simulator/message/${chatbotId}`, 
        payload,
        { timeout: 120000 } // 2 minutes timeout to wait for backend redirection
      );
        
        console.log("🔵 [SIMULATOR] Réponse brute du backend :", response.data);

        // Compatibilité : le backend renvoie soit un tableau (ancien format),
        // soit { messages: [...], griotLogs: [...] } (nouveau format avec logs Griot).
        let backendMessages = response.data;
        let backendGriotLogs = [];
        if (response.data && !Array.isArray(response.data) && typeof response.data === 'object') {
            backendMessages = response.data.messages || [];
            backendGriotLogs = response.data.griotLogs || [];
        }

        // Ingestion des logs Griot AI de cette session de test
        if (Array.isArray(backendGriotLogs) && backendGriotLogs.length > 0) {
            ingestGriotLogs(backendGriotLogs);
        }

        if (Array.isArray(backendMessages)) {
            console.log(`🔵 [SIMULATOR] ${backendMessages.length} messages reçus`);
            for (const msg of backendMessages) {
               await processBackendMessage(msg);
            }
            // Aucun message rendu mais des logs d'erreur Griot : prévenir l'utilisateur
            if (backendMessages.length === 0 && hasGriotError.value) {
                addSystemMessage("Le bot n'a pas répondu. Consultez les logs Griot AI ci-dessous pour comprendre pourquoi.");
                showLogs.value = true;
            }
        }

    } catch (error) {
        console.error("Simulation error:", error);
        addSystemMessage("Erreur de simulation: " + (error.response?.data?.error || error.message));
    } finally {
        isTyping.value = false;
        // Check if we are waiting for input (if last message was a question/button)
        // This is heuristic, backend doesn't explicitly say "waiting_input"
        // But if we received buttons/list, we probably are.
    }
};

const processBackendMessage = async (msgWrapper) => {
    // msgWrapper is { params: {...}, phone_number_id, ... }
    const params = msgWrapper.params;
    console.log("🔵 [SIMULATOR] Traitement du message :", params);

    if (!params) return;

    // Simulate typing delay per message
    isTyping.value = true;
    await new Promise(r => setTimeout(r, 600));
    isTyping.value = false;

    if (params.type === 'text') {
        addBotMessage(params.text.body, 'text');
    } else if (params.type === 'image') {
        addBotMessage(params.image.link || params.image.url /* fallback */, 'image');
    } else if (params.type === 'interactive') {
        const interactive = params.interactive;
        console.log('[SIM] Interactive Payload:', interactive);
        console.log('[SIM] Interactive Type:', interactive.type);
        
        if (interactive.type === 'button') {
            const buttons = interactive.action.buttons.map(b => ({
                id: b.reply.id,
                label: b.reply.title,
                action: b,
                replyType: 'button_reply'
            }));
            addBotMessage(interactive.body.text, 'buttons', buttons);
            isWaitingForInput.value = true;
        } else if (interactive.type === 'list') {
             // Safe access to sections and rows
             const sections = interactive.action.sections || [];
             const rows = [];
             
             sections.forEach(sec => {
                 if (sec && sec.rows) {
                     sec.rows.forEach(r => {
                         rows.push({
                             id: r.id,
                             label: r.title,
                             action: r,
                             replyType: 'list_reply'
                         });
                     });
                 }
             });

             addBotMessage(interactive.body.text, 'buttons', rows); // reusing buttons UI for list for now
             isWaitingForInput.value = true;
        }
    } else if (params.type === 'template') {
        // Fallback for templates (just show name)
        addBotMessage(`[Template: ${params.template.name}]`, 'text');
    } else if (params.type === 'location_request_message') {
         addBotMessage(params.interactive.body.text, 'text');
         // This typically requires location input, treat as text for now
         isWaitingForInput.value = true;
    }
};

const getOwnerPhone = () => {
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return user.phone_number || user.tel || '221776625746';
        }
    } catch(e) {}
    return '221776625746';
};

const handleOptionClick = async (btn, msg) => {
  // Allow clicking even if input is not "waiting" (user can change mind/fork flow)
  
  // Mark specific button as clicked
  btn.clicked = true;
  
  // Find the message ID to use as context
  let contextMessageId = msg?.id || "wamid.HBgMMjIxNzc1MDY0NDU2FQIAERgSODU0RDY2RkU4ODFERDMzMzAxAA==";
  // Fallback to last if msg not passed (should not happen with new template)
  if (!msg) {
       for (let i = messages.value.length - 1; i >= 0; i--) {
          if (messages.value[i].sender === 'bot' && messages.value[i].id) {
              contextMessageId = messages.value[i].id;
              break;
          }
       }
  }

  isWaitingForInput.value = false;
  addUserMessage(btn.label);
  
  const ownerPhone = getOwnerPhone();
  
  // Determine Reply Type (default to button_reply for backward comaptibility)
  console.log('🔴 [DEBUG] btn object:', JSON.stringify(btn, null, 2));
  console.log('🔴 [DEBUG] btn.replyType:', btn.replyType);
  const replyType = btn.replyType || 'button_reply';
  console.log('🔴 [DEBUG] Final replyType:', replyType);
  
  let interactivePayload = {};

  if (replyType === 'list_reply') {
      interactivePayload = {
          type: 'list_reply',
          list_reply: {
              id: btn.id,
              title: btn.label,
              description: btn.action?.description || '' 
          }
      };
  } else {
      interactivePayload = {
          type: 'button_reply', // Fixed string
          button_reply: {
              id: btn.id,
              title: btn.label
          }
      };
  }

  // Specific Structure Request
  await sendToBackendSimulation({
      context: {
          from: props.chatbot.display_phone_number || "221761758092",
          id: contextMessageId
      },
      type: 'interactive',
      interactive: interactivePayload
  });
};

// Keep scrollToBottom and toggleChat...


const scrollToBottom = () => {
  nextTick(() => {
    const container = document.querySelector('.messages-area');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  });
};

const toggleChat = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    loadChatHistory();
  }
};

const loadChatHistory = async () => {
  try {
    const chatbotId = props.chatbot._id || props.chatbot.id;
    const ownerPhone = getOwnerPhone();
    
    // Switch to Production/Local URL based on environment (currently keeping local for dev)
    // const baseUrl = 'https://webhook.peelo.io'; 
    const baseUrl = 'http://localhost:1436';

    // 1. Try SessionStorage first (Client-side persistence)
    const sessionKey = `sim_chat_${chatbotId}`;
    const saved = sessionStorage.getItem(sessionKey);
    if (saved) {
        try {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) {
                 messages.value = parsed.map(msg => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
                 scrollToBottom();
                 return; // Use local session if available
            }
        } catch(e) {}
    }

    // 2. If no session, try Backend
    const response = await axios.post(`${baseUrl}/pipeline/simulator/history/${chatbotId}`, {
      tel: ownerPhone
    });

    if (response.data && Array.isArray(response.data)) {
      // Clear existing messages only if we got a valid history response (or empty array)
      messages.value = response.data.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp) // Ensure Date object
      }));
      scrollToBottom();
    }
  } catch (error) {
    console.error("Failed to load chat history:", error);
    // Don't show error to user, just start empty or keep existing
  }
};

// --- Watchers ---

// --- Watchers ---

watch(() => props.startBlockId, async (newId) => {
   if (newId) {
       // Auto-open chat if closed
       if (!isOpen.value) {
         isOpen.value = true;
       }
       // Only load history if chat is empty (prevent overwriting active conversation)
       if (messages.value.length === 0) {
           await loadChatHistory();
       }
   }
}, { immediate: true });

// Auto-save to SessionStorage
watch(messages, (newVal) => {
    const chatbotId = props.chatbot?._id || props.chatbot?.id;
    if(chatbotId && newVal.length > 0) {
        sessionStorage.setItem(`sim_chat_${chatbotId}`, JSON.stringify(newVal));
    }
}, { deep: true });

onMounted(() => {
  if (props.startBlockId) {
    loadChatHistory();
  }
});



</script>

<style scoped>
.playground-chat-wrapper {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.quick-action-btn {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 20px;
  background: #f0f2f5;
  border: 1px solid #e1e4e8;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4a4a4a;
  transition: all 0.2s ease;
  font-weight: 500;
}

.quick-action-btn:hover {
  background: #e4e6eb;
  color: #1a1a1a;
  border-color: #d1d4d8;
}

.quick-action-btn svg {
  opacity: 0.7;
}

/* Bubble Button */
.chat-bubble-btn {
  width: 65px;
  height: 65px;
  border-radius: 50%;
  background: #0E8A3E;
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(14, 138, 62, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.chat-bubble-btn:hover {
  transform: scale(1.1);
  background: #0b6e31;
}

.chat-bubble-btn.active {
  background: #333;
  transform: rotate(90deg);
}

/* Chat Window */
.chat-window {
  width: 400px;
  height: 650px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.05);
}

/* Header */
.chat-header {
  padding: 15px;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bot-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.header-text h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.status-indicator {
  font-size: 12px;
  color: #0E8A3E;
  display: flex;
  align-items: center;
  gap: 4px;
}

.dot {
  width: 6px;
  height: 6px;
  background: #0E8A3E;
  border-radius: 50%;
}

.action-btn {
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background 0.2s;
}

.action-btn:hover {
  background: #f5f5f5;
  color: #333;
}

/* Messages Area */
.messages-area {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-top: 50%;
}

.message-row {
  display: flex;
  width: 100%;
}

.message-row.bot { justify-content: flex-start; }
.message-row.user { justify-content: flex-end; }
.message-row.system { justify-content: center; }

.message-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
}

.message-bubble.bot {
  background: white;
  color: #333;
  border-bottom-left-radius: 2px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  border: 1px solid #e5e5e5;
}

/* Disable clickable links in message text */
.message-text {
  word-break: break-word;
}

.message-text a,
.message-bubble a {
  color: inherit;
  text-decoration: none;
  pointer-events: none;
  cursor: default;
}

.message-bubble.user {
  background: #0E8A3E;
  color: white;
  border-bottom-right-radius: 2px;
  box-shadow: 0 1px 2px rgba(14, 138, 62, 0.2);
}

.message-bubble.system {
  background: transparent;
  color: #999;
  font-size: 12px;
  padding: 5px;
  box-shadow: none;
}

.message-image img {
  max-width: 100%;
  border-radius: 8px;
}

.message-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.buttons-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.chat-option-btn {
  background: white;
  border: 1px solid #0E8A3E;
  color: #0E8A3E;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  flex-grow: 1;
}

.chat-option-btn:hover {
  background: #0E8A3E;
  color: white;
}

.chat-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #ccc;
  color: #999;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: white;
  border-radius: 14px;
  width: fit-content;
  border-bottom-left-radius: 2px;
  border: 1px solid #e5e5e5;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  background: #ccc;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* Input Area */
.input-area {
  padding: 10px;
  background: white;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  align-items: center;
}

.input-area input {
  flex: 1;
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  padding: 10px 15px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.input-area input:focus {
  border-color: #0E8A3E;
}

.input-area input:disabled {
  background: #f9f9f9;
}

.send-btn {
  background: #0E8A3E;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.send-btn:hover {
  background: #0b6e31;
}

.send-btn:disabled {
  background: #e5e5e5;
  cursor: not-allowed;
}

/* Logs Griot AI button (header) */
.logs-btn {
  position: relative;
}
.logs-btn.active {
  color: #0E8A3E;
  background: #eafaf0;
}
.logs-btn.has-error {
  color: #d93025;
}
.logs-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #d93025;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

/* Griot AI Logs Panel */
.griot-logs-panel {
  background: #1e1e2e;
  color: #e0e0e0;
  border-top: 1px solid #11111b;
  max-height: 240px;
  display: flex;
  flex-direction: column;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.griot-logs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #181825;
  border-bottom: 1px solid #11111b;
}

.griot-logs-title {
  font-size: 12px;
  font-weight: 700;
  color: #cdd6f4;
  display: flex;
  align-items: center;
  gap: 6px;
}

.griot-logs-count {
  background: #313244;
  color: #a6adc8;
  border-radius: 8px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
}

.griot-logs-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.griot-logs-filter {
  background: #313244;
  color: #cdd6f4;
  border: 1px solid #45475a;
  border-radius: 4px;
  font-size: 11px;
  padding: 2px 4px;
  outline: none;
}

.griot-logs-clear {
  background: transparent;
  color: #a6adc8;
  border: 1px solid #45475a;
  border-radius: 4px;
  font-size: 11px;
  padding: 2px 8px;
  cursor: pointer;
}
.griot-logs-clear:hover {
  background: #45475a;
  color: #fff;
}

.griot-logs-body {
  overflow-y: auto;
  padding: 6px 8px;
  flex: 1;
}

.griot-logs-empty {
  color: #6c7086;
  font-size: 11px;
  padding: 10px;
  text-align: center;
}

.griot-log-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  line-height: 1.5;
  padding: 2px 4px;
  border-radius: 3px;
}
.griot-log-line.level-error { background: rgba(217, 48, 37, 0.12); }
.griot-log-line.level-warn { background: rgba(250, 179, 25, 0.10); }

.griot-log-time {
  color: #6c7086;
  flex-shrink: 0;
}

.griot-log-level {
  font-weight: 700;
  flex-shrink: 0;
  min-width: 42px;
}
.griot-log-level.level-debug { color: #89b4fa; }
.griot-log-level.level-info { color: #a6e3a1; }
.griot-log-level.level-warn { color: #f9e2af; }
.griot-log-level.level-error { color: #f38ba8; }

.griot-log-step {
  color: #cba6f7;
  background: #313244;
  border-radius: 3px;
  padding: 0 4px;
  flex-shrink: 0;
}

.griot-log-msg {
  color: #cdd6f4;
  word-break: break-word;
}

.griot-log-data {
  color: #7f849c;
  word-break: break-all;
  width: 100%;
  padding-left: 48px;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
